import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import classNames from "classnames";
import styles from "./PieChart.module.css";

interface PieChartComponentProps {
  legen?: boolean;
  /** Smaller chart for dense layouts (e.g. summary SWOT card). */
  size?: "default" | "compact";
  data: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  tooltipFormatter?: (value: number, name: string) => [string, string];
}

/**
 * PieChartComponent for displaying pie chart with adaptive width for mobile.
 *
 * @param data - Chart data with name, value, and color.
 * @param legen - Show legend or not.
 * @param tooltipFormatter - Custom formatter for tooltip values. Default: (value: number) => [`${value}%`, "Percentage"]
 */
export const PieChartComponent: React.FC<PieChartComponentProps> = ({
  data,
  legen = true,
  size = "default",
  tooltipFormatter = (value: number, name: string) => [`${value}`, name],
}) => {
  const compact = size === "compact";
  const innerRadius = compact ? 30 : 40;
  const outerRadius = compact ? 62 : 80;

  return (
    <div
      className={classNames(styles.chartContainer, {
        [styles.compact]: compact,
      })}
    >
      <div className={styles.pieArea}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              formatter={(value, name) =>
                tooltipFormatter(Number(value ?? 0), String(name ?? ""))
              }
              labelFormatter={(label) => (label == null ? "" : String(label))}
              contentStyle={{
                backgroundColor: "var(--app-surface)",
                border: "1px solid var(--app-border)",
                borderRadius: "4px",
                color: "var(--app-text-heading)",
              }}
            />
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
              label={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            {legen && (
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{
                  paddingTop: "20px",
                  color: "var(--app-text-heading)",
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
